import { Controller, useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect } from "react";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, FormLabel, IconButton, styled } from "@mui/material";
import { ModalContainer } from "../../../../../../common/components/modal_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { FormSwitchInput } from "../../../../../../common/components/masked_inputs/react_forms/form_switch_input";
import { ControleServicosForm, ControleServicosSchema } from "../../../schemas/ControleServicos_lista/ControleServicos";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import { ModalLoading } from "../../../../../../common/components/modal_loading";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

type Props = {
  open: boolean;
  saving: boolean;
  onClose: () => void;
  onSubmit: (form: ControleServicosForm) => void;
  dados?: ControleServicosForm | null;
  editmod: boolean;
};

export const ModalControleServicos = ({ open, onClose, saving, onSubmit, dados, editmod }: Props) => {
  const hookForm = useForm<ControleServicosForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(ControleServicosSchema),
  });

  useEffect(() => {
    if (dados) {
      hookForm.reset({ ...dados });
    } else {
      hookForm.reset({
        // valores padrão para criação
        ativo: true,
        nroServico: "",
        descricao: "",
      });
    }
  }, [dados, hookForm, open]);

  const [nomeArquivo] = hookForm.watch(["nomeArquivo"]);

  const handleSubmit = useCallback(
    (form: ControleServicosForm) => {
      if (!saving) onSubmit(form);
    },
    [saving, onSubmit],
  );

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [saving, onClose]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, field: any) => {
    const file = e.target.files?.[0];
    if (!file) return;

    hookForm.setValue("nomeArquivo", file.name);
    field.onChange(file);
  };

  const handleRemoveFile = (field: any) => {
    field.onChange(undefined);
    hookForm.setValue("nomeArquivo", undefined);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            component: "form",
            onSubmit: hookForm.handleSubmit(handleSubmit),
          },
        }}
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": { width: "100%", maxWidth: "40rem" },
          },
        }}
      >
        <DialogTitle>Serviços Controlados</DialogTitle>
        <DialogContent>
          <ModalLoading loading={saving}>
            <ModalContainer>
              <div className="flex flex-col gap-2">
                <FormSwitchInput name="ativo" readOnly={saving} reactHookForm={hookForm} />

                <DefaultFormInput name="nroServico" label="Nº" reactHookForm={hookForm} required={false} />

                <DefaultFormInput name="descricao" label="Serviço" reactHookForm={hookForm} required={true} multiline minRows={4} fullWidth />

                <Controller
                  name="arquivo"
                  control={hookForm.control}
                  render={({ field }) => (
                    <div className="">
                      {!nomeArquivo ? (
                        <PrimaryButton component="label" variant="contained" disabled={saving}>
                          Escolher arquivo
                          <VisuallyHiddenInput type="file" onChange={(e) => handleFileChange(e, field)} />
                        </PrimaryButton>
                      ) : (
                        <div className="flex items-center gap-3">
                          <span
                            onClick={() => {
                              if (!editmod) return;
                              const file = hookForm.getValues("arquivo");
                              if (file) {
                                const url = URL.createObjectURL(file);
                                const link = document.createElement("a");
                                link.href = url;
                                link.download = hookForm.getValues("nomeArquivo") || "arquivo";
                                link.click();
                                URL.revokeObjectURL(url);
                              }
                            }}
                            className={editmod ? "cursor-pointer" : "cursor-not-allowed"}
                          >
                            {nomeArquivo}
                          </span>
                          <IconButton onClick={() => handleRemoveFile(field)}>
                            <DynamicIcon nameIcon="DeleteForeverRounded" size={22} className="text-red-500" />
                          </IconButton>
                        </div>
                      )}
                    </div>
                  )}
                />
              </div>
            </ModalContainer>
          </ModalLoading>
        </DialogContent>
        <DialogActions>
          <Box className="w-full flex justify-end gap-4 p-4">
            <Box className="flex gap-1">
              <Box className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                <h1 className="text-sm text-black dark:text-white" onClick={handleClose}>
                  Cancelar
                </h1>
              </Box>
              <Box width="120px">
                {saving ? (
                  <PrimaryButton buttonColor="primary-green">{"Salvando..."}</PrimaryButton>
                ) : (
                  <PrimaryButton type="submit" buttonColor="primary-green">
                    {"Salvar"}
                  </PrimaryButton>
                )}
              </Box>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};
